import { Drawer, Stack, Typography, useTheme } from "@mui/material";
import { Pen, X } from "lucide-react";
import type { ReactNode } from "react";
import { DrawerButton } from "../Forms/Buttons/DrawerButton";

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  disabled?: boolean;
  showEditButton?: boolean;
  onEdit?: () => void;
}

export const SideDrawer = ({
  open,
  onClose,
  title,
  children,
  disabled = false,
  showEditButton = false,
  onEdit,
}: SideDrawerProps) => {
  const { colors, customSpacing, radius, customFontWeight, blurs, width } =
    useTheme();

  const handleClose = () => {
    if (!disabled) {
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent",
        },
        "& .MuiDrawer-paper": {
          width: width.md,
          maxWidth: "100%",
          backdropFilter: blurs.md,
          WebkitBackdropFilter: blurs.md,
          backgroundColor: "#00000005",
          boxShadow: "none",
          borderRadius: radius["2xl"],
          border: `1px solid ${colors.border.primary}`,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          p: 0,
          position: "absolute",
          top: customSpacing.xl,
          right: customSpacing.xl,
          bottom: customSpacing.xl,
          height: "auto",
        },
      }}
    >
      <Stack
        sx={{
          height: "100%",
          position: "relative",
          flex: 1,
          minHeight: 0,
          padding: customSpacing.xl,
        }}
        spacing={customSpacing.xl}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            pb: 0,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: colors.text.primary,
              fontWeight: customFontWeight.bold,
            }}
          >
            {title}
          </Typography>
          <Stack direction="row" spacing={customSpacing.xs}>
            {showEditButton && onEdit && (
              <DrawerButton icon={Pen} onClick={onEdit} disabled={disabled} />
            )}
            <DrawerButton icon={X} onClick={handleClose} disabled={disabled} />
          </Stack>
        </Stack>
        {children}
      </Stack>
    </Drawer>
  );
};
